'use client';

import {
  forwardRef,
  memo,
  ReactNode,
  useImperativeHandle,
  useRef,
} from 'react';
import { Color, IUniform, ShaderMaterial, Uniform, Vector3 } from 'three';

import promiseTimeline from 'utils/promiseTimeline';

import { useEarthTextures } from './loadTextures';
import fragmentShader from './shaders/fragment.glsl';
import vertexShader from './shaders/vertex.glsl';

type Vector3Tuple = [x: number, y: number, z: number];

const ROTATION_TO_ZERO_COORDINATES: Vector3Tuple = [0, Math.PI / 2, 0];
const SCALE: Vector3Tuple = [0.9, 0.9, 0.9];

export interface GlobeRef {
  playOutro: () => Promise<any>;
  playWatchAgain: () => Promise<any>;
  sunDirection: IUniform<Vector3>;
}

interface GlobeProps {
  geometry: ReactNode;
  sunDirection: Vector3;
  dayColor: Color;
  twilightColor: Color;
}

const Globe = forwardRef<GlobeRef, GlobeProps>(
  ({ geometry, sunDirection, dayColor, twilightColor }, ref) => {
    const { earthDayTexture, earthNightTexture, earthSpecularCloudsTexture } =
      useEarthTextures();

    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
        uniforms: {
          uDayTexture: new Uniform(earthDayTexture),
          uNightTexture: new Uniform(earthNightTexture),
          uSpecularCloudsTexture: new Uniform(earthSpecularCloudsTexture),
          uSunDirection: new Uniform(sunDirection),
          uAtmosphereDayColor: new Uniform(dayColor),
          uAtmosphereTwilightColor: new Uniform(twilightColor),
          uNightFactor: { value: 1.0 },
          uOutroFactor: { value: 0.0 },
        },
      })
    );

    useImperativeHandle(ref, () => ({
      sunDirection: materialRef.current.uniforms.uSunDirection,
      playOutro: () => {
        return promiseTimeline(materialRef.current.uniforms.uOutroFactor, {
          value: 1,
          duration: 2,
        });
      },
      playWatchAgain: () => {
        return promiseTimeline(materialRef.current.uniforms.uOutroFactor, {
          value: 0,
          duration: 2,
        });
      },
    }));

    return (
      <mesh
        scale={SCALE}
        rotation={ROTATION_TO_ZERO_COORDINATES}
        frustumCulled={false}
      >
        {geometry}
        <primitive object={materialRef.current} attach="material" />
      </mesh>
    );
  }
);

Globe.displayName = 'Globe';
export default memo(Globe);
